import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { WebPurchaseAction } from '@/features/account/components/WebPurchaseAction';
import { WebReservationAction } from '@/features/account/components/WebReservationAction';
import { SectionCard } from '@/components/SectionCard';
import {
  getCatalogSupport,
  getPublicDevice,
  type PublicCatalogDeviceDetail,
  type PublicCatalogSupport,
} from '@/features/operations/api';
import { DeviceTrustBlock } from '@/features/operations/components/DeviceTrustBlock';
import { PublicDeviceGallery } from '@/features/operations/components/PublicDeviceGallery';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import { buildDeviceInquiryMessage, buildWhatsAppUrl } from '@/features/operations/whatsapp';

type PageState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'error' }
  | { status: 'ready'; device: PublicCatalogDeviceDetail };

function BackToCatalog() {
  return (
    <Button
      component={RouterLink}
      to="/catalog"
      startIcon={<ArrowBackIcon />}
      color="inherit"
      sx={{ alignSelf: 'flex-start' }}
    >
      Volver al catálogo
    </Button>
  );
}

/** Public sheet of one published device (CAT-2). The backend decides what is public (BR-031). */
export function PublicDevicePage() {
  const { id = '' } = useParams();
  const { formatNullablePrice } = usePriceDisplay();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [requestVersion, setRequestVersion] = useState(0);
  // Warranty and WhatsApp number back the sheet but do not gate it: if they cannot be read, the
  // device still shows, with the backing text and without those two parts.
  const [support, setSupport] = useState<PublicCatalogSupport | null>(null);

  useEffect(() => {
    let active = true;
    getCatalogSupport()
      .then((value) => {
        if (active) setSupport(value);
      })
      .catch(() => {
        if (active) setSupport(null);
      });
    return () => {
      active = false;
    };
  }, [requestVersion]);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    getPublicDevice(id)
      .then((device) => {
        if (active) setState(device ? { status: 'ready', device } : { status: 'unavailable' });
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [id, requestVersion]);

  if (state.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="secondary" aria-label="Cargando el equipo" />
      </Box>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
          Este equipo ya no está disponible.
        </Alert>
        <Button component={RouterLink} to="/catalog" variant="contained">
          Ver los equipos disponibles
        </Button>
      </Stack>
    );
  }

  if (state.status === 'error') {
    return (
      <Stack spacing={2}>
        <BackToCatalog />
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setRequestVersion((v) => v + 1)}>
              Reintentar
            </Button>
          }
        >
          No pudimos cargar el equipo. Intentá de nuevo.
        </Alert>
      </Stack>
    );
  }

  const { device } = state;
  const deviceName = `${device.brand} ${device.model}`;
  const whatsappUrl = support?.whatsappNumber
    ? buildWhatsAppUrl(
        support.whatsappNumber,
        buildDeviceInquiryMessage(device, `${window.location.origin}/catalog/${device.id}`),
      )
    : null;
  const specs = [
    { label: 'Capacidad', value: device.storageGb === null ? null : `${device.storageGb} GB` },
    { label: 'Color', value: device.color },
    {
      label: 'Batería',
      value: device.batteryHealth === null ? null : `${device.batteryHealth}%`,
    },
    ...device.characteristics,
  ].filter((spec): spec is { label: string; value: string } => Boolean(spec.value));

  return (
    <Stack spacing={3}>
      <BackToCatalog />
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <PublicDeviceGallery photos={device.photos} deviceName={deviceName} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography
                variant="overline"
                color="primary.main"
                sx={{ letterSpacing: '0.12em', fontWeight: 800 }}
              >
                USADO CERTIFICADO
              </Typography>
              <Typography variant="h2" component="h1">
                {deviceName}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="h4" component="p">
                {formatNullablePrice(device.salePriceMinor)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Precio final, IVA incluido.
              </Typography>
            </Stack>
            <WebReservationAction
              deviceId={device.id}
              deviceName={deviceName}
              priceMinor={device.salePriceMinor}
              onUnavailable={() => setRequestVersion((v) => v + 1)}
              secondaryAction={(askToVerify) => (
                <WebPurchaseAction
                  deviceId={device.id}
                  deviceName={deviceName}
                  priceMinor={device.salePriceMinor}
                  onUnavailable={() => setRequestVersion((v) => v + 1)}
                  onNeedsVerification={() => askToVerify('comprar')}
                />
              )}
            />
            {/* Hidden without a configured number: a button to nobody would be a dead end. */}
            {whatsappUrl ? (
              <Button
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="inherit"
                startIcon={<WhatsAppIcon />}
                fullWidth
              >
                Consultar por WhatsApp
              </Button>
            ) : null}
            <SectionCard title="Características">
              <Stack divider={<Divider flexItem />} spacing={1.5} component="dl" sx={{ m: 0 }}>
                {specs.map((spec) => (
                  <Stack
                    key={spec.label}
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography component="dt" color="text.secondary">
                      {spec.label}
                    </Typography>
                    <Typography component="dd" sx={{ m: 0, fontWeight: 600, textAlign: 'right' }}>
                      {spec.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </SectionCard>
            <DeviceTrustBlock warranty={support?.warranty ?? null} />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
